// src/pages/WheelScreen.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { createSpinReward, checkActiveRewardBlock } from '../lib/discounts';

// ── UTC+1 week helpers ────────────────────────────────────────────────────────
function getUTC1Now() {
  return new Date(Date.now() + 60 * 60 * 1000);
}

function getSundayMidnightUTC1() {
  const now = getUTC1Now();
  const day = now.getUTCDay(); // 0=Sun
  const sunday = new Date(now);
  sunday.setUTCDate(now.getUTCDate() - day);
  sunday.setUTCHours(0, 0, 0, 0);
  return sunday;
}

function hasHadSpinThisWeek(lastSpinAt) {
  if (!lastSpinAt) return false;
  const last = new Date(new Date(lastSpinAt).getTime() + 60 * 60 * 1000);
  const sunday = getSundayMidnightUTC1();
  return last >= sunday;
}

function getNextSundayMidnightLocal() {
  const sunday = getSundayMidnightUTC1();
  const next = new Date(sunday.getTime() + 7 * 24 * 60 * 60 * 1000);
  return new Date(next.getTime() - 60 * 60 * 1000); // back to UTC
}

// ── Weighted random pick ──────────────────────────────────────────────────────
function pickReward(rewards) {
  const total = rewards.reduce((s, r) => s + (r.weight || 1), 0);
  let rand = Math.random() * total;
  for (const r of rewards) { rand -= r.weight; if (rand <= 0) return r; }
  return rewards[rewards.length - 1];
}

// ── Color helpers ─────────────────────────────────────────────────────────────
function adjustColor(hex, amt) {
  const n = parseInt((hex || '#4cca74').replace('#', ''), 16);
  const clamp = v => Math.min(255, Math.max(0, v));
  const r = clamp((n >> 16) + amt);
  const g = clamp(((n >> 8) & 0xff) + amt);
  const b = clamp((n & 0xff) + amt);
  return `#${[r,g,b].map(v=>v.toString(16).padStart(2,'0')).join('')}`;
}

// ── Canvas wheel draw ─────────────────────────────────────────────────────────
function drawWheel(canvas, rewards, rotation) {
  if (!canvas || !rewards.length) return;
  const ctx = canvas.getContext('2d');
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const r  = cx - 6;
  const arc = (Math.PI * 2) / rewards.length;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Outer glow ring
  ctx.beginPath();
  ctx.arc(cx, cy, r + 5, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(76,202,116,.25)';
  ctx.lineWidth = 3;
  ctx.stroke();

  rewards.forEach((seg, i) => {
    const start = rotation + i * arc;
    const end   = start + arc;
    const fill  = i % 2 === 0 ? (seg.color || '#4cca74') : adjustColor(seg.color || '#4cca74', -35);

    // Segment
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, start, end);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = '#0a0a0a';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Label
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(start + arc / 2);
    ctx.textAlign = 'right';
    ctx.fillStyle = '#fff';
    const fs = canvas.width < 280 ? 9 : canvas.width < 340 ? 11 : 13;
    ctx.font = `700 ${fs}px "Barlow Condensed", sans-serif`;
    ctx.shadowColor = 'rgba(0,0,0,.8)';
    ctx.shadowBlur = 4;
    ctx.fillText(`${seg.emoji || '🎁'} ${seg.label}`, r - 12, 5);
    ctx.restore();
  });

  // Center disc
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 28);
  grad.addColorStop(0, '#1a1a1a');
  grad.addColorStop(1, '#0a0a0a');
  ctx.beginPath();
  ctx.arc(cx, cy, 28, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = '#4cca74';
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Center star
  ctx.fillStyle = '#4cca74';
  ctx.font = '16px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowBlur = 0;
  ctx.fillText('✦', cx, cy);

  // Fixed pointer at top
  ctx.beginPath();
  ctx.moveTo(cx - 12, 2);
  ctx.lineTo(cx + 12, 2);
  ctx.lineTo(cx, 28);
  ctx.closePath();
  ctx.fillStyle = '#4cca74';
  ctx.shadowColor = 'rgba(76,202,116,.6)';
  ctx.shadowBlur = 8;
  ctx.fill();
  ctx.shadowBlur = 0;
}

// ── Confetti particle system ─────────────────────────────────────────────────
class Particle {
  constructor(x, y) {
    this.x = x; this.y = y;
    this.vx = (Math.random() - 0.5) * 8;
    this.vy = Math.random() * -12 - 4;
    this.gravity = 0.4;
    this.color = ['#4cca74','#f0a500','#4a9edd','#e05050','#9b59b6'][Math.floor(Math.random()*5)];
    this.size = Math.random() * 8 + 4;
    this.rotation = Math.random() * 360;
    this.rotSpeed = (Math.random() - 0.5) * 10;
    this.life = 1;
    this.decay = Math.random() * 0.015 + 0.008;
  }
  update() {
    this.x += this.vx;
    this.vy += this.gravity;
    this.y += this.vy;
    this.rotation += this.rotSpeed;
    this.life -= this.decay;
  }
}

function launchConfetti(canvasEl) {
  if (!canvasEl) return;
  const ctx = canvasEl.getContext('2d');
  const cx = canvasEl.width / 2;
  const cy = canvasEl.height / 4;
  const particles = Array.from({ length: 80 }, () => new Particle(cx + (Math.random()-0.5)*80, cy));
  let raf;
  const loop = () => {
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    particles.forEach(p => {
      p.update();
      if (p.life <= 0) return;
      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.fillRect(-p.size/2, -p.size/2, p.size, p.size * 0.6);
      ctx.restore();
    });
    if (particles.some(p => p.life > 0)) raf = requestAnimationFrame(loop);
  };
  loop();
  return () => cancelAnimationFrame(raf);
}

// ── Countdown to next Sunday ─────────────────────────────────────────────────
function useCountdown(targetDate) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = targetDate - Date.now();
      if (diff <= 0) { setLabel(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      const d = Math.floor(h / 24);
      if (d > 0) setLabel(`${d}d ${h % 24}h`);
      else setLabel(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return label;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function WheelScreen() {
  const { user, profile, showToast, refreshProfile } = useApp();
  const navigate = useNavigate();

  const [rewards, setRewards]         = useState([]);
  const [settings, setSettings]       = useState(null);
  const [spinning, setSpinning]       = useState(false);
  const [result, setResult]           = useState(null);
  const [wonCode, setWonCode]         = useState('');
  const [wonLabel, setWonLabel]       = useState('');
  const [rotation, setRotation]       = useState(-Math.PI / 2);
  const [loading, setLoading]         = useState(true);
  const [spinBalance, setSpinBalance] = useState(0);
  const [showModal, setShowModal]     = useState(false);

  // Anti-abuse: holds the existing active reward if user is blocked from spinning
  const [activeRewardBlock, setActiveRewardBlock] = useState(null); // null | reward object
  const [blockModalOpen, setBlockModalOpen]       = useState(false);

  const canvasRef      = useRef(null);
  const confettiRef    = useRef(null);
  const rafRef         = useRef(null);
  const weeklyGranted  = useRef(false);

  const nextSunday    = getNextSundayMidnightLocal();
  const countdown     = useCountdown(nextSunday);

  // Load rewards + settings + active reward check
  useEffect(() => {
    const loadData = async () => {
      const [r, s] = await Promise.all([
        supabase.from('wheel_rewards').select('*').eq('active', true).order('sort_order'),
        supabase.from('wheel_settings').select('*').eq('id', 1).single(),
      ]);
      setRewards(r.data || []);
      setSettings(s.data);
      setLoading(false);
    };
    loadData();
  }, []);

  // Check for existing active reward whenever user is known
  useEffect(() => {
    if (!user) return;
    checkActiveRewardBlock(user.id).then(({ blocked, reward }) => {
      setActiveRewardBlock(blocked ? reward : null);
    });
  }, [user]);

  // Sync spin balance
  useEffect(() => { setSpinBalance(profile?.spin_balance ?? 0); }, [profile]);

  // Weekly spin grant (once per mount, after profile loads)
  useEffect(() => {
    if (!user || !profile || weeklyGranted.current) return;
    if (hasHadSpinThisWeek(profile.last_weekly_spin_at)) return;
    weeklyGranted.current = true;
    const newBal = (profile.spin_balance || 0) + 1;
    supabase.from('profiles')
      .update({ spin_balance: newBal, last_weekly_spin_at: new Date().toISOString() })
      .eq('id', user.id)
      .then(({ error }) => {
        if (!error) {
          setSpinBalance(newBal);
          showToast('🎰 Weekly spin added!', 'success');
        }
      });
  }, [user, profile, showToast]);

  // Draw wheel
  useEffect(() => {
    drawWheel(canvasRef.current, rewards, rotation);
  }, [rotation, rewards]);

  // Cleanup
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const handleSpin = useCallback(async () => {
    if (!user) { navigate('/login'); return; }
    if (spinBalance < 1) { showToast('No spins remaining', 'error'); return; }
    if (spinning || !rewards.length) return;

    // ── Anti-abuse pre-spin gate (fresh DB check, not stale state) ──────────
    const { blocked, reward } = await checkActiveRewardBlock(user.id);
    if (blocked) {
      setActiveRewardBlock(reward);
      setBlockModalOpen(true);
      return;  // abort spin before touching spin_balance
    }

    setSpinning(true);
    setResult(null);
    setWonCode('');
    setShowModal(false);

    const won = pickReward(rewards);
    const arc = (Math.PI * 2) / rewards.length;
    const wonIdx = rewards.findIndex(r => r.id === won.id);
    const segMid = wonIdx * arc + arc / 2;

    // spin 6–10 full rotations, land pointer at top (-PI/2) aligned to won segment
    const fullSpins = (6 + Math.random() * 4) * Math.PI * 2;
    const targetRot = rotation + fullSpins - ((rotation + fullSpins + segMid + Math.PI / 2) % (Math.PI * 2));

    const duration = 5500;
    const startRot = rotation;
    const startTime = performance.now();

    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      // Quintic ease-out for premium feel
      const ease = 1 - Math.pow(1 - t, 5);
      setRotation(startRot + (targetRot - startRot) * ease);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setRotation(targetRot);
        finishSpin(won);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
  }, [user, spinning, rewards, spinBalance, rotation, navigate, showToast]);

  const finishSpin = useCallback(async (won) => {
    try {
      // 1. Deduct spin
      const newBalance = spinBalance - 1;
      await supabase.from('profiles').update({
        spin_balance: newBalance,
        total_spins_used: (profile?.total_spins_used || 0) + 1,
      }).eq('id', user.id);
      setSpinBalance(newBalance);

      // 2. Create reward
      let code = null;
      if (won.type !== 'spins') {
        code = await createSpinReward(user.id, won.type, won.value, won.label);
        setWonCode(code);
        setWonLabel(won.label);
      }

      // 3. Extra spins reward
      if (won.type === 'spins') {
        const add = Math.round(won.value) || 1;
        await supabase.from('profiles').update({ spin_balance: newBalance + add }).eq('id', user.id);
        setSpinBalance(newBalance + add);
        setWonLabel(`+${add} Spin${add !== 1 ? 's' : ''}`);
      }

      // 4. Log history
      await supabase.from('wheel_history').insert({
        user_id:       user.id,
        reward_id:     won.id,
        reward_label:  won.label,
        reward_type:   won.type,
        reward_value:  won.value,
        discount_code: code,
      });

      // Clear any stale block state — user just created a new reward
      setActiveRewardBlock(null);
      setResult(won);
      setShowModal(true);

      // Launch confetti
      setTimeout(() => launchConfetti(confettiRef.current), 100);

      await refreshProfile();
    } catch (err) {
      if (err.blocked) {
        // Active reward already exists — refund the deducted spin
        const refunded = spinBalance; // spinBalance was decremented above — restore it
        await supabase.from('profiles').update({
          spin_balance:     refunded,
          total_spins_used: Math.max(0, (profile?.total_spins_used || 0)),
        }).eq('id', user.id);
        setSpinBalance(refunded);
        setActiveRewardBlock(err.reward);
        setBlockModalOpen(true);
      } else {
        showToast('Error saving reward: ' + err.message, 'error');
      }
    } finally {
      setSpinning(false);
    }
  }, [user, profile, spinBalance, showToast, refreshProfile]);

  const goToCheckout = () => {
    navigate('/checkout');
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (!user) return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎰</div>
      <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2.4rem', letterSpacing: '.05em', marginBottom: 12 }}>Reward Wheel</h1>
      <p style={{ color: '#666', fontSize: '.92rem', lineHeight: 1.8, marginBottom: 28 }}>
        Log in to spin the wheel and win exclusive discounts every week.
      </p>
      <button className="btn-primary" onClick={() => navigate('/login')} style={{ minWidth: 180 }}>
        Sign In to Spin
      </button>
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  if (settings && !settings.enabled) return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>⏸️</div>
      <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2rem', letterSpacing: '.05em' }}>Wheel Paused</h1>
      <p style={{ color: '#666', marginTop: 12 }}>The reward wheel is temporarily unavailable. Check back soon!</p>
    </div>
  );

  const size = Math.min(typeof window !== 'undefined' ? window.innerWidth - 48 : 340, 340);

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 20px 100px', textAlign: 'center' }}>

      {/* Header */}
      <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', display: 'block', marginBottom: 8 }}>
        Rewards
      </span>
      <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(2rem,6vw,3rem)', letterSpacing: '.04em', marginBottom: 6 }}>
        {settings?.title || 'Spin to Win!'}
      </h1>
      <p style={{ color: '#666', fontSize: '.88rem', lineHeight: 1.7, marginBottom: 24, maxWidth: 360, margin: '0 auto 24px' }}>
        {settings?.subtitle || 'Spin the wheel for exclusive discounts — free spin every Sunday!'}
      </p>

      {/* Spin balance pill */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: spinBalance > 0 ? 'rgba(76,202,116,.1)' : 'rgba(255,255,255,.05)',
          border: `1px solid ${spinBalance > 0 ? 'rgba(76,202,116,.3)' : 'rgba(255,255,255,.1)'}`,
          borderRadius: 25, padding: '8px 18px',
        }}>
          <span>🎰</span>
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, color: spinBalance > 0 ? '#4cca74' : '#555' }}>
            {spinBalance} spin{spinBalance !== 1 ? 's' : ''} remaining
          </span>
        </div>
        {spinBalance < 1 && countdown && (
          <div style={{ fontSize: '.75rem', color: '#444', fontFamily: "'Barlow Condensed',sans-serif" }}>
            Next in {countdown}
          </div>
        )}
      </div>

      {/* Wheel */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
        <div style={{ position: 'relative', width: size, height: size }}>
          <canvas
            ref={canvasRef}
            width={size}
            height={size}
            style={{
              borderRadius: '50%',
              display: 'block',
              boxShadow: '0 0 40px rgba(76,202,116,.15), 0 8px 40px rgba(0,0,0,.5)',
              transition: spinning ? 'none' : 'box-shadow .3s',
            }}
          />
        </div>
      </div>

      {/* Spin button */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <button
          className="btn-primary"
          onClick={handleSpin}
          disabled={spinning || spinBalance < 1 || !!activeRewardBlock}
          style={{
            fontSize: '1.05rem', padding: '14px 48px',
            minWidth: 220, letterSpacing: '.08em',
            opacity: (spinning || !!activeRewardBlock) ? .6 : 1,
            transform: spinning ? 'scale(.98)' : 'scale(1)',
            transition: 'transform .2s, opacity .2s',
          }}
        >
          {spinning
            ? '🌀 Spinning…'
            : activeRewardBlock
              ? '🎁 Reward Active — Use It First'
              : spinBalance < 1
                ? '🎰 No Spins Left'
                : '🎰 Spin Now!'}
        </button>

        {/* Active reward nudge */}
        {activeRewardBlock && !spinning && (
          <div style={{
            background: 'rgba(155,89,182,.08)',
            border: '1px solid rgba(155,89,182,.3)',
            borderRadius: 12, padding: '12px 18px',
            maxWidth: 320, textAlign: 'center',
          }}>
            <div style={{ fontSize: '.8rem', color: '#9b59b6', fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
              🎁 Active Reward
            </div>
            <div style={{ fontSize: '.82rem', color: '#ccc', marginBottom: 8 }}>
              {activeRewardBlock.label || 'You have an active reward'} — use it at checkout before spinning again.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/checkout')}
                style={{ background: '#9b59b6', border: 'none', borderRadius: 20, padding: '7px 16px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 800, letterSpacing: '.06em', cursor: 'pointer' }}
              >
                Use at Checkout
              </button>
              <button
                onClick={() => setBlockModalOpen(true)}
                style={{ background: 'none', border: '1px solid rgba(155,89,182,.3)', borderRadius: 20, padding: '7px 14px', color: '#9b59b6', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Details
              </button>
            </div>
          </div>
        )}

        {!activeRewardBlock && spinBalance < 1 && (
          <p style={{ fontSize: '.8rem', color: '#555', maxWidth: 280, lineHeight: 1.6 }}>
            Complete an order or sign up for the newsletter to earn spins.
            {countdown && <><br /><strong style={{ color: '#4cca74' }}>Free weekly spin in {countdown}</strong></>}
          </p>
        )}
      </div>

      {/* How to earn */}
      <div style={{ marginTop: 48, background: '#111', border: '1px solid rgba(255,255,255,.07)', borderRadius: 16, padding: 20, textAlign: 'left' }}>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 14 }}>
          How to Earn Spins
        </div>
        {[
          { icon: '📅', text: 'Free spin every Sunday at midnight (UTC+1)' },
          { icon: '📦', text: '+1 spin after completing an order' },
          { icon: '✉️', text: '+1 spin when you sign up for the newsletter' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, fontSize: '.85rem' }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{item.icon}</span>
            <span style={{ color: '#777' }}>{item.text}</span>
          </div>
        ))}
      </div>

      {/* ── Active reward block modal ── */}
      {blockModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9001,
          background: 'rgba(0,0,0,.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
          animation: 'fadeIn .2s ease',
        }}>
          <div style={{
            background: '#0e0e0e',
            border: '1px solid rgba(155,89,182,.4)',
            borderRadius: 24, padding: '36px 28px',
            maxWidth: 400, width: '100%',
            textAlign: 'center',
            boxShadow: '0 0 60px rgba(155,89,182,.15)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>🎁</div>

            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: '.72rem', fontWeight: 700,
              letterSpacing: '.2em', textTransform: 'uppercase',
              color: '#9b59b6', marginBottom: 8,
            }}>
              Active Reward
            </div>

            <div style={{
              fontFamily: "'Bebas Neue',sans-serif",
              fontSize: '1.9rem', letterSpacing: '.04em',
              color: '#f4f4f4', marginBottom: 16, lineHeight: 1.1,
            }}>
              You already have an active reward
            </div>

            <p style={{ fontSize: '.88rem', color: '#777', lineHeight: 1.8, marginBottom: 20 }}>
              Use or let your current reward expire before spinning again.
              Only one active reward is allowed at a time.
            </p>

            {/* Current reward details */}
            {activeRewardBlock && (
              <div style={{
                background: 'rgba(155,89,182,.08)',
                border: '1px dashed rgba(155,89,182,.3)',
                borderRadius: 12, padding: '14px 18px',
                marginBottom: 20,
              }}>
                <div style={{ fontSize: '.72rem', color: '#9b59b6', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                  Current Reward
                </div>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '1rem', fontWeight: 700, color: '#f4f4f4', marginBottom: 4 }}>
                  {activeRewardBlock.label || 'Discount Reward'}
                </div>
                {activeRewardBlock.discount_code && (
                  <div style={{ fontFamily: 'monospace', fontSize: '.88rem', color: '#9b59b6', letterSpacing: '.12em', marginBottom: 4 }}>
                    {activeRewardBlock.discount_code}
                  </div>
                )}
                {activeRewardBlock.expires_at && (
                  <div style={{ fontSize: '.72rem', color: '#555' }}>
                    Expires {new Date(activeRewardBlock.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn-primary"
                onClick={() => { setBlockModalOpen(false); navigate('/checkout'); }}
                style={{ minWidth: 160, background: '#9b59b6', borderColor: '#9b59b6' }}
              >
                Use at Checkout →
              </button>
              <button
                onClick={() => setBlockModalOpen(false)}
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,.12)',
                  borderRadius: 25, padding: '12px 20px',
                  color: '#555', cursor: 'pointer',
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: '.85rem', fontWeight: 700,
                  minWidth: 100,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Result modal overlay ── */}
      {showModal && result && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
          animation: 'fadeIn .25s ease',
        }}>
          {/* Confetti canvas */}
          <canvas
            ref={confettiRef}
            width={Math.min(480, window.innerWidth - 40)}
            height={500}
            style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}
          />

          <div style={{
            background: '#0e0e0e', border: '1px solid rgba(76,202,116,.3)',
            borderRadius: 24, padding: '36px 28px', maxWidth: 400, width: '100%',
            textAlign: 'center', position: 'relative', zIndex: 1,
            boxShadow: '0 0 60px rgba(76,202,116,.15)',
          }}>
            <div style={{ fontSize: '3.5rem', marginBottom: 12 }}>{result.emoji}</div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.72rem', fontWeight: 700, letterSpacing: '.2em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 6 }}>
              You Won
            </div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2.4rem', letterSpacing: '.04em', color: '#f4f4f4', marginBottom: 20 }}>
              {wonLabel || result.label}
            </div>

            {wonCode && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: '.8rem', color: '#666', marginBottom: 10 }}>
                  Your reward code — saved to your account:
                </div>
                <div style={{
                  background: '#111', border: '2px dashed #4cca74', borderRadius: 12,
                  padding: '16px 20px', marginBottom: 10,
                  fontFamily: 'monospace', fontSize: '1.5rem', fontWeight: 700,
                  color: '#4cca74', letterSpacing: '.15em',
                  userSelect: 'all',
                }}>
                  {wonCode}
                </div>
                <div style={{ fontSize: '.75rem', color: '#555' }}>
                  ✓ Saved to My Rewards · Valid 30 days · Auto-applies at checkout
                </div>
              </div>
            )}

            {result.type === 'spins' && (
              <div style={{ background: 'rgba(76,202,116,.08)', borderRadius: 12, padding: '14px 20px', marginBottom: 20, color: '#4cca74', fontSize: '.9rem' }}>
                🎰 Extra spins added to your balance!
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              {wonCode && (
                <button
                  className="btn-primary"
                  onClick={goToCheckout}
                  style={{ minWidth: 160 }}
                >
                  Use Now at Checkout →
                </button>
              )}
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none', border: '1px solid rgba(255,255,255,.15)',
                  borderRadius: 25, padding: '12px 24px', color: '#666',
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  cursor: 'pointer', fontSize: '.85rem', minWidth: 120,
                }}
              >
                {spinBalance > 0 ? 'Spin Again' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}